import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AuthorGrid from '../components/AuthorGrid';
import { useAuthors } from '../hooks/useAuthors';
import { useLanguage } from '../contexts/LanguageContext';
import { translate } from '../i18n';

const introCopy: Record<'en' | 'zh', { kicker: string; paragraph: string }> = {
  en: {
    kicker: 'The artisans behind the brilliance',
    paragraph:
      'Discover the master craftsmen and designers who breathe life into every Sonsen creation. Their devotion to perfection — from delicate carving to inspired design — carries forward a legacy of Eastern artistry and timeless elegance.'
  },
  zh: {
    kicker: '璀璨背后的匠心',
    paragraph:
      '走近淞圣的工艺大师与设计师，他们以精妙绝伦的雕刻技艺和蕴含人文精神的美学设计，赋予每件作品全新的风貌与生命力，传承东方瑰宝的百年风华。'
  }
};

const AuthorPage = () => {
  const { locale } = useLanguage();
  const { authors, loading, error } = useAuthors();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryParam = searchParams.get('q') ?? '';
  const authorIdParam = searchParams.get('authorId');
  const [filter, setFilter] = useState(queryParam);
  const intro = introCopy[locale];

  useEffect(() => {
    setFilter(queryParam);
  }, [queryParam]);

  useEffect(() => {
    if (!authorIdParam || authors.length === 0) {
      return;
    }

    const next = new URLSearchParams(searchParams);
    next.delete('authorId');

    const match = authors.find((author) => author.id === authorIdParam);
    if (!match) {
      setSearchParams(next, { replace: true });
      return;
    }

    const name = match.name[locale] || match.name.en;
    setFilter(name);

    if (name.trim()) {
      next.set('q', name);
    } else {
      next.delete('q');
    }
    setSearchParams(next, { replace: true });
  }, [authorIdParam, authors, locale, searchParams, setSearchParams]);

  const handleFilterChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFilter(value);

    const next = new URLSearchParams(searchParams);
    next.delete('authorId');
    if (value.trim()) {
      next.set('q', value);
    } else {
      next.delete('q');
    }
    setSearchParams(next, { replace: true });
  };

  const filteredAuthors = useMemo(() => {
    const normalized = filter.trim().toLowerCase();
    if (!normalized) {
      return authors;
    }

    return authors.filter((author) => {
      const english = author.name.en.toLowerCase();
      const chinese = author.name.zh.toLowerCase();
      return english.includes(normalized) || chinese.includes(normalized);
    });
  }, [authors, filter]);

  return (
    <div className="author-page">
      <section className="author-hero">
        <p className="author-hero__kicker">{intro.kicker}</p>
        <h1>{translate(locale, 'authors')}</h1>
        <p className="author-hero__body">{intro.paragraph}</p>
      </section>

      <div className="author-filter">
        <label htmlFor="author-filter-input">{translate(locale, 'authorFilterLabel')}</label>
        <input
          id="author-filter-input"
          type="search"
          value={filter}
          onChange={handleFilterChange}
          placeholder={translate(locale, 'authorFilterPlaceholder')}
          disabled={loading}
          autoComplete="off"
        />
      </div>

      {loading ? (
        <p className="empty-state">{translate(locale, 'authorsLoadingState')}</p>
      ) : error ? (
        <p className="empty-state">{translate(locale, 'authorsErrorState')}</p>
      ) : filteredAuthors.length > 0 ? (
        <AuthorGrid authors={filteredAuthors} />
      ) : (
        <p className="empty-state">{translate(locale, 'authorsEmptyState')}</p>
      )}
    </div>
  );
};

export default AuthorPage;
